/** UXP-00006 — AI Creative Studio framework (mirrors pkg/creativestudio) */

export const UXP_CR_ID = 'UXP-00006';

export interface DashboardHealthItem {
  label: string;
  score: number;
  status: 'healthy' | 'warning' | 'error' | 'info';
}

export interface ValidationItem {
  label: string;
  passed: boolean;
  score: number;
  detail: string;
}

export const DASHBOARD_METRICS = [
  'Creative Health', 'Visual Quality', 'Brand Consistency', 'Marketplace Readiness',
  'CTR Prediction', 'Conversion Prediction', 'Creative Opportunities', 'Publishing Status', 'AI Confidence',
];

export const ASSET_TYPES = [
  'Hero Images', 'Lifestyle Images', 'Infographics', 'Comparison Charts', 'Feature Graphics',
  'Packaging Images', 'Size Guides', 'Premium A+', 'Videos', 'Brand Store Assets', 'Social Assets', 'Marketing Assets',
];

export const DIRECTOR_OUTPUTS = [
  'Creative Strategy', 'Creative Brief', 'Visual Hierarchy', 'Composition', 'Lighting',
  'Camera Angle', 'Typography', 'Color Palette', 'Lifestyle Direction', 'Emotional Positioning',
];

export const HERO_FEATURES = [
  'Marketplace Compliance', 'White Background', 'Safe Area Validation', 'Cropping',
  'Lighting', 'Shadow Rules', 'Reflection Rules', 'Camera Angles',
];

export const LIFESTYLE_SCENES = [
  'Professional Models', 'Lifestyle Scenes', 'Festival Themes', 'Luxury Scenes',
  'Regional Variants', 'International Variants', 'Office', 'Travel', 'Fashion', 'Outdoor', 'Indoor',
];

export const INFOGRAPHIC_TYPES = [
  'Feature Highlights', 'Specifications', 'Dimensions', 'Materials', 'Benefits',
  'Usage Instructions', 'Comparison Graphics', 'Icons', 'Callouts',
];

export const PREMIUM_APLUS_TYPES = [
  'Premium Modules', 'Brand Story', 'Comparison Modules',
  'Lifestyle Banners', 'Collection Pages', 'Interactive-ready Assets',
];

export const VIDEO_TYPES = [
  'Product Videos', 'Lifestyle Videos', '360° Views', 'Explainers',
  'Animated Infographics', 'Brand Stories', 'Marketplace Videos',
];

export const BRAND_STORE_TYPES = [
  'Store Homepage', 'Collection Pages', 'Seasonal Campaign Pages',
  'Landing Pages', 'Category Pages', 'Hero Banners',
];

export const VISUAL_DNA_DIMENSIONS = [
  'Visual Identity', 'Composition Rules', 'Lighting Rules', 'Typography Rules', 'Iconography',
  'Color Palette', 'Human Model Rules', 'Background Rules', 'Consistency Score',
];

export const PROMPT_RULES = [
  'Global Rules', 'Organization Rules', 'Brand Rules', 'Customer Intent DNA', 'Product DNA',
  'Marketplace Rules', 'Image Rules', 'Campaign Rules', 'Negative Rules',
];

export const VALIDATION_CHECKS = [
  'Marketplace Compliance', 'Image Resolution', 'Quality', 'Composition', 'Brand Consistency',
  'Text Usage', 'Visual Hierarchy', 'CTR Prediction', 'Conversion Prediction', 'AI Confidence',
];

export const EXPERIMENT_TYPES = [
  'Hero Image Testing', 'Lifestyle Testing', 'Infographic Testing', 'Packaging Testing',
  'Model Testing', 'Color Testing', 'Expected ROI Simulation',
];

export const BULK_OPERATIONS = [
  'Bulk Generation', 'Bulk Review', 'Bulk Approval', 'Bulk Publishing', 'Release Packages', 'Rollback',
];

export const COLLABORATION_FEATURES = [
  'Comments', 'Annotations', 'Assignments', 'Approvals', 'Version Comparison', 'Creative History',
];

export const ASSET_MGMT_FEATURES = [
  'Versions', 'Collections', 'Tags', 'Search', 'Duplicate Detection',
  'Similarity Detection', 'Publishing', 'Archival',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Creative Dashboard', 'Product Creative Workspace', 'AI Creative Director',
  'Visual DNA Engine', 'Hero & Lifestyle Studios', 'Infographic · A+ · Video',
  'Quality Validation', 'Experimentation', 'Bulk Creative Factory',
  'Asset Management', 'Publishing & Analytics',
];

export const ANALYTICS_METRICS = [
  'Creative Performance', 'CTR Lift', 'Conversion Lift', 'Marketplace Acceptance',
  'AI Success Rate', 'Creative ROI', 'Business Impact',
];

export const LIVE_ROUTES: Record<string, string> = {
  creativeStudio: '/ai/creative',
  contentIntel: '/amazon/content-intel',
  productIntel: '/platform/product-intelligence',
};

export const DEMO_DASHBOARD_HEALTH: DashboardHealthItem[] = [
  { label: 'Creative Health', score: 86, status: 'healthy' },
  { label: 'Visual Quality', score: 82, status: 'healthy' },
  { label: 'Brand Consistency', score: 91, status: 'healthy' },
  { label: 'Marketplace Readiness', score: 88, status: 'healthy' },
  { label: 'CTR Prediction', score: 74, status: 'warning' },
  { label: 'Conversion Prediction', score: 79, status: 'healthy' },
  { label: 'Creative Opportunities', score: 8, status: 'info' },
  { label: 'Publishing Status', score: 92, status: 'healthy' },
  { label: 'AI Confidence', score: 87, status: 'healthy' },
];

export const DEMO_VALIDATION: ValidationItem[] = [
  { label: 'Marketplace Compliance', passed: true, score: 95, detail: 'White background · safe area OK' },
  { label: 'Image Resolution', passed: true, score: 100, detail: '2000×2000px · exceeds minimum' },
  { label: 'Quality', passed: true, score: 88, detail: 'Sharp focus · no artifacts' },
  { label: 'Composition', passed: true, score: 84, detail: 'Product fills 85% of frame' },
  { label: 'Brand Consistency', passed: true, score: 91, detail: 'Matches Visual DNA palette' },
  { label: 'Text Usage', passed: false, score: 45, detail: 'Text overlay exceeds 20% threshold' },
  { label: 'Visual Hierarchy', passed: true, score: 86, detail: 'Product is focal point' },
  { label: 'CTR Prediction', passed: true, score: 74, detail: 'Estimated +12% vs current hero' },
  { label: 'Conversion Prediction', passed: true, score: 79, detail: 'Lifestyle variant recommended' },
  { label: 'AI Confidence', passed: true, score: 87, detail: 'High confidence generation' },
];

export const DEMO_DIRECTOR_BRIEF = [
  'Strategy: Premium audio positioning with lifestyle context for urban professionals',
  'Visual hierarchy: Product hero 60% · lifestyle context 30% · brand mark 10%',
  'Lighting: Soft diffused key light, subtle rim light for product separation',
  'Color palette: Deep navy (#1A2B4A) · warm gold accent (#C9A962) · white space',
  'Emotional positioning: Confidence, sophistication, everyday luxury',
];

export const DEMO_VISUAL_DNA: Record<string, string> = {
  'Visual Identity': 'Minimal premium · clean lines · product-forward',
  'Composition Rules': 'Rule of thirds · 85% product fill · centered hero',
  'Lighting Rules': 'Soft diffused · no harsh shadows · rim accent',
  'Typography Rules': 'Sans-serif · max 2 weights · no hero text overlay',
  Iconography: 'Line icons · 2px stroke · rounded corners',
  'Color Palette': 'Navy #1A2B4A · Gold #C9A962 · White #FFFFFF',
  'Human Model Rules': 'Diverse · 25-40 age · professional casual',
  'Background Rules': 'White for hero · contextual for lifestyle',
  'Consistency Score': '91% across 847 product assets',
};

export const DEMO_ASSET_COUNTS: Record<string, number> = {
  'Hero Images': 847, 'Lifestyle Images': 623, 'Infographics': 412,
  'Comparison Charts': 156, 'Feature Graphics': 289, 'Packaging Images': 98,
  'Size Guides': 67, 'Premium A+': 234, 'Videos': 89,
  'Brand Store Assets': 45, 'Social Assets': 178, 'Marketing Assets': 92,
};

export function buildCreativeStudioDashboard() {
  return {
    dashboard_metrics: DASHBOARD_METRICS.length,
    asset_types: ASSET_TYPES.length,
    director_outputs: DIRECTOR_OUTPUTS.length,
    lifestyle_scenes: LIFESTYLE_SCENES.length,
    visual_dna_dimensions: VISUAL_DNA_DIMENSIONS.length,
    prompt_rules: PROMPT_RULES.length,
    validation_checks: VALIDATION_CHECKS.length,
  };
}
